import { api } from './index'

// VERA-914 / VRG-105 (Stage B): the "Leads" lens of the People page. A Lead is a
// person (identified by the email they left on a scan) who is NOT yet an owner of
// this company — see `GET /owners/leads` (apps/api owners.admin.controller.ts).

// The lead's first-touch certificate (their EARLIEST collecting scan) — shaped for
// the shared CertificateCell (id + product.title/images).
export interface LeadCertificate {
  id: string
  product: {
    title: string
    images: string[]
  }
}

// A distinct owner whose certificate this lead scanned — shaped for the shared
// UserGroupCell (id/name/first_name/last_name/email/avatar).
export interface LeadReferrer {
  id: string
  name: string | null
  first_name: string | null
  last_name: string | null
  email: string
  avatar: string | null
}

export interface Lead {
  email: string
  name: string | null
  registered: boolean
  certificate: LeadCertificate | null
  referrers: LeadReferrer[]
  scans: number
  firstSeen: string
  lastSeen: string
  // "City, Country" of the lead's most recent scan. Null if that scan has no geo data.
  lastScanLocation: string | null
  // VRG-720 — what the lead shared about themselves on the access form (typed, or
  // parsed from a contact card they tapped in). All three come from one submission;
  // null is "not shared", which is the common case since they are optional.
  company: string | null
  title: string | null
  phone: string | null
  // VRG-720 — every distinct certificate this lead scanned, most recent first, max 25.
  // Ids only; the transfer picker resolves them. Distinct from `certificate` above,
  // which is the FIRST-touch one the Certificate column attributes them to.
  scannedCertificateIds: string[]
}

export type LeadSortField = 'lastSeen' | 'firstSeen' | 'scans' | 'email'
export type LeadSortOrder = 'asc' | 'desc'

export interface GetLeadsParams {
  page?: number
  size?: number
  q?: string
  sort?: LeadSortField
  order?: LeadSortOrder
  // Owner id — Owners tab "Leads" count → Leads tab deep link (?tab=leads&referrer=<id>).
  referrer?: string
  // VERA-914 / VRG-105 (Stage B) — Leads tab filters.
  registered?: boolean
  lastScannedFrom?: string
  lastScannedTo?: string
}

export interface GetLeadsResponse {
  page: number
  size: number
  totalItems: number
  totalPages: number
  items: Lead[]
}

export const leadApi = {
  getLeads: async (params: GetLeadsParams): Promise<GetLeadsResponse> => {
    const response = await api.get('/owners/leads', { params })
    return response.data
  },
}
